import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { taxCodesOf, type TaxCodeRow } from './format';

/**
 * The group premium summary as the screen reads it (SDD-021 §8.1, PC-15, PC-19).
 *
 * Every figure is the service's. An adjustment that does not apply is still LISTED — with its
 * reason, and for a tier the next one and the count it is measured against — so the screen can
 * say "Mengenrabatt ab 100 aktiven Mitgliedern (aktuell 1)" instead of leaving the operator to
 * wonder why a discount the agreement names is missing. Billing ignores those entries.
 */

export const NOT_APPLIED_REASONS = [
  'BELOW_LOWEST_TIER',
  'NO_AGREEMENT_VALUE',
  'MINIMUM_MET',
] as const;
export type NotAppliedReason = (typeof NOT_APPLIED_REASONS)[number];

export interface NotAppliedNote {
  /** A reason the console words, or `OTHER` (shown by its code). */
  readonly reason: NotAppliedReason | 'OTHER';
  readonly code: string;
  /** BELOW_LOWEST_TIER: the next tier's threshold, its value, and the count measured. */
  readonly min?: number;
  readonly value?: string;
  readonly count?: number;
}

/** Why an adjustment did not apply — undefined for one that did. */
export function notAppliedNoteOf(
  adjustment: InstanceApi.PremiumAdjustment,
): NotAppliedNote | undefined {
  if (adjustment.applied === true) return undefined;
  const code = adjustment.reason ?? '';
  const known = (NOT_APPLIED_REASONS as readonly string[]).includes(code);
  return {
    reason: known ? (code as NotAppliedReason) : 'OTHER',
    code,
    ...(adjustment.basis?.nextTier?.min === undefined
      ? {}
      : { min: adjustment.basis.nextTier.min }),
    ...(adjustment.basis?.nextTier?.value === undefined
      ? {}
      : { value: adjustment.basis.nextTier.value }),
    ...(adjustment.basis?.activeMembers === undefined
      ? {}
      : { count: adjustment.basis.activeMembers }),
  };
}

/** Applied first, then the ones that did not apply — each group in the service's order. */
export function orderedAdjustments(
  adjustments: readonly InstanceApi.PremiumAdjustment[] | undefined,
): InstanceApi.PremiumAdjustment[] {
  const list = [...(adjustments ?? [])];
  return [
    ...list.filter((adjustment) => adjustment.applied === true),
    ...list.filter((adjustment) => adjustment.applied !== true),
  ];
}

/** The tax codes the summary's figures name — the rows of the per-code tables. */
export const summaryTaxCodesOf = (
  summary: InstanceApi.GroupPremiumSummary | undefined,
): TaxCodeRow[] =>
  taxCodesOf(
    summary?.members?.tax,
    summary?.total?.tax,
    ...(summary?.adjustments ?? []).map((adjustment) => adjustment.tax),
  );

/** True when the summary is for a period (`basis.periodFrom`/`periodTo`), not a day. */
export const isPeriodSummary = (summary: InstanceApi.GroupPremiumSummary | undefined): boolean =>
  (summary?.basis?.periodFrom ?? '') !== '' && (summary?.basis?.periodTo ?? '') !== '';
